/**
 * Deep-link auto-impersonation (Jake, 2026-10-27).
 *
 * Opening a link to a RESOURCE page (a store, an order, a client, a portal
 * approval...) first puts the signed-in user in the seat that can see it:
 *
 *   - Super Admin + distributor page -> impersonate the owning distributor
 *   - Super Admin + portal page      -> impersonate the owning client
 *   - distributor + portal page of one of ITS clients -> view as that client
 *   - distributor + its own distributor page -> just go (leaving a view-as)
 *   - client + its own portal page   -> just go
 *   - anything else (another distributor's store, a missing row, a refused
 *     start) -> the caller's normal role landing, with no error notice; a
 *     distributor viewing as a client leaves the view-as first, so it lands
 *     on its own dashboard (Jake's rule is about the person, not the seat)
 *
 * A seat the start endpoint would refuse (`owner.enterable === false`) never
 * costs the caller the seat it already holds. Every automatic stop/start
 * carries a hint the server turns into the audit marker `via: "deeplink"`.
 *
 * Lists, dashboards and settings carry no owner lookup and are untouched
 * (resource pages only). Runs after sign-in (the `next` return path) and on
 * the first navigation of every page load while signed in.
 *
 * Which routes are resource pages is declared ON THE ROUTE RECORD:
 *   meta: { deeplinkOwner: { type: '<registry type>', param: '<param name>' } }
 * The backend registry (`api/src/endpoints/deeplink_owner.py` `_REGISTRY`)
 * maps the type to its owner. The router stays the only table of URLs.
 *
 * Mechanism: GET /api/auth/deeplink-owner answers who owns the resource,
 * which seat the caller holds, and whom the token is impersonating now. This
 * module decides (a pure function) and, for a switch, drives the EXISTING
 * impersonation store actions -- their endpoints keep their own capability
 * checks and audit rows. A switch changes the capability set, so it ends in a
 * hard navigation to the link, like every other impersonation start.
 *
 * Loop guard: before that hard navigation the target path is written to
 * sessionStorage. The next load consumes it; if the seat is STILL wrong there,
 * we land instead of switching again. At most one automatic switch per
 * navigation, and a refused start or resolver ends on the role landing.
 */
import { isSafeReturnPath } from './postLoginRedirect'

export const DEEPLINK_SWITCH_GUARD_KEY = 'deeplinkAutoSwitchTarget'

// A Postgres integer id: no sign, no leading zero, at most 10 digits.
const ID_RE = /^[1-9][0-9]{0,9}$/

const PROCEED = Object.freeze({ action: 'proceed' })
const LANDING = Object.freeze({ action: 'landing' })
const OWNER_KINDS = new Set(['distributor', 'client'])

/**
 * The owner lookup a resolved route declares, or null.
 *
 * @param {object|null} route a resolved route location (`to`, or
 *   `router.resolve(path)`)
 * @returns {{type: string, id: string} | null}
 */
export function deeplinkTargetFor(route) {
    const spec = route?.meta?.deeplinkOwner
    if (!spec || typeof spec.type !== 'string' || typeof spec.param !== 'string') {
        return null
    }
    const raw = route.params?.[spec.param]
    if (typeof raw !== 'string' || !ID_RE.test(raw)) return null
    return { type: spec.type, id: raw }
}

/**
 * Decide what to do with a resolved deep link. Pure.
 *
 * @param {object} params
 * @param {object|null} params.resolution the resolver's 200 body
 *   `{actor, owner: {kind, id, name}, impersonating: {kind, id}}`, or null
 *   when it refused.
 * @param {boolean} [params.alreadySwitched] an automatic switch for this same
 *   target already happened on the previous load.
 * @returns {{action: 'proceed'} | {action: 'landing'} |
 *   {action: 'switch', stop: boolean,
 *    start: null | {via: 'distributor'|'client'|'view_as_client', id: number, name: string}}}
 */
export function decideDeeplinkAction({ resolution, alreadySwitched = false } = {}) {
    const owner = resolution?.owner
    if (!owner || !OWNER_KINDS.has(owner.kind) || owner.id == null) return LANDING
    const current = resolution.impersonating || { kind: null, id: null }
    const impersonating = current.kind != null
    const seated = current.kind === owner.kind && current.id === owner.id
    const name = owner.name || ''

    let start
    switch (resolution.actor) {
        case 'superadmin':
            if (seated) return PROCEED
            start = { via: owner.kind === 'distributor' ? 'distributor' : 'client', id: owner.id, name }
            break
        case 'distributor':
            if (owner.kind === 'distributor') {
                // Its own page: the right seat is its own identity.
                if (!impersonating) return PROCEED
                start = null
            } else {
                if (seated) return PROCEED
                start = { via: 'view_as_client', id: owner.id, name }
            }
            break
        case 'client':
            return owner.kind === 'client' && !impersonating ? PROCEED : LANDING
        default:
            return LANDING
    }
    // The start endpoint would refuse this seat (a disabled or Owner-less
    // distributor, an inactive or portal-less client): keep the seat the
    // caller holds instead of stopping it for a start that cannot succeed.
    if (start && owner.enterable === false) return LANDING
    if (alreadySwitched) return LANDING
    return { action: 'switch', stop: impersonating, start }
}

// Every automatic stop/start sends this, and the server tags the audit row
// `via: "deeplink"` (it writes the value itself; see impersonationService).
const DEEPLINK_HINT = Object.freeze({ deeplink: true })

const START_ACTION = {
    distributor: 'startImpersonation',
    client: 'startClientImpersonation',
    view_as_client: 'startViewAsCustomer',
}

/**
 * Resolve and apply a deep link for a signed-in user.
 *
 * @param {object} route resolved route location (meta + params + path + fullPath)
 * @param {object} deps
 * @param {function} deps.resolveOwner (type, id) -> axios-style promise
 * @param {object} deps.impersonation the impersonation store (hydrate,
 *   stopImpersonation, startImpersonation, startClientImpersonation,
 *   startViewAsCustomer)
 * @param {object} deps.storage sessionStorage-like (the loop guard)
 * @param {function} deps.hardNavigate (path) -> void, a full page load
 * @returns {Promise<'proceed'|'landing'|'reloading'>}
 *   proceed   -- carry on to the route as is
 *   landing   -- send the user to their role landing instead
 *   reloading -- a hard navigation is in flight; do not navigate
 */
export async function applyDeeplinkImpersonation(route, deps) {
    // The open-redirect allow-list applies before anything is resolved.
    if (!route || !isSafeReturnPath(route.fullPath)) return 'proceed'
    const target = deeplinkTargetFor(route)
    if (!target) return 'proceed'

    const { storage } = deps
    let alreadySwitched = false
    try {
        alreadySwitched = storage.getItem(DEEPLINK_SWITCH_GUARD_KEY) === route.path
        storage.removeItem(DEEPLINK_SWITCH_GUARD_KEY)
    } catch (_e) {
        // Storage unavailable: no guard to read, and none can be written
        // below either -- the switch still runs at most once per load.
    }

    let resolution = null
    let refused = false
    try {
        const resp = await deps.resolveOwner(target.type, target.id)
        resolution = resp?.data ?? null
    } catch (err) {
        const status = err?.response?.status
        if (status === 404) {
            // Not yours, or missing (one answer, by design).
            refused = true
        } else {
            // 429 / 5xx / network: the link is dropped for the role landing,
            // so say why. A fixed string and the bare status only -- never
            // the error object (it carries the request config).
            console.warn(
                '[deeplink] owner lookup unavailable; opening the role landing instead. HTTP status:',
                typeof status === 'number' ? status : 0
            )
        }
    }

    const imp = deps.impersonation
    if (refused && !isPortalPath(route.path)) {
        // A distributor viewing as one of its clients opened a distributor-app
        // page it cannot see (another distributor's store, a missing row).
        // Jake's rule keys on the PERSON: a distributor goes to its own
        // dashboard, so leave the view-as rather than strand it on the
        // client's portal home. An SA client impersonation carries no
        // view-as breadcrumb and is left alone.
        if (await leaveViewAsForRefusedPage(imp, deps)) return 'reloading'
    }

    const decision = decideDeeplinkAction({ resolution, alreadySwitched })
    if (decision.action !== 'switch') return decision.action

    try {
        if (decision.stop) {
            // The store is memory-only; hydrate so stop() picks the active mode.
            await imp.hydrate()
            await imp.stopImpersonation(DEEPLINK_HINT)
        }
        if (decision.start) {
            await imp[START_ACTION[decision.start.via]](
                decision.start.id,
                decision.start.name,
                DEEPLINK_HINT
            )
        }
    } catch (_e) {
        // A refused stop/start may still have changed the seat (a stop that
        // succeeded before the start was refused), so the in-memory
        // capabilities are not trustworthy: load the neutral entry fresh,
        // which lands on the role home. Never the link again (no loop).
        deps.hardNavigate('/')
        return 'reloading'
    }

    try {
        storage.setItem(DEEPLINK_SWITCH_GUARD_KEY, route.path)
    } catch (_e) {
        // See above.
    }
    deps.hardNavigate(route.fullPath)
    return 'reloading'
}

function isPortalPath(path) {
    return path === '/portal' || String(path || '').startsWith('/portal/')
}

/**
 * @returns {Promise<boolean>} true when a hard navigation to "/" is in flight
 */
async function leaveViewAsForRefusedPage(imp, deps) {
    try {
        await imp.hydrate()
    } catch (_e) {
        return false
    }
    if (!imp.isViewAsCustomer) return false
    try {
        await imp.stopImpersonation(DEEPLINK_HINT)
    } catch (_e) {
        // The seat is uncertain either way; the fresh load below re-reads it.
    }
    deps.hardNavigate('/')
    return true
}

export default applyDeeplinkImpersonation
